import React from 'react'
import { useAppDispatch } from '@SonarCX/hooks/redux'

import Notes from './Notes'
import NoteEditor from './NoteEditor'

import { replaceWithBr } from '@SonarCX/components/organisms/LexicalEditor'
import { track } from '@SonarCX/lib/mixpanel'
import { getMedia, uploadFileTask } from '@SonarCX/lib/api'
import { AddErrorAlert } from '@SonarCX/slices/alert'
import { useAreYouSure } from '@SonarCX/providers/AreYouSureProvider'

export const FILE_EXTENSION = '.SONAR'

type TaskNoteProps = {
  values: LocalTask
  setValues: React.Dispatch<React.SetStateAction<LocalTask>>
  triggerSave: () => void
  setChanged: React.Dispatch<React.SetStateAction<boolean>>
  inModal?: boolean
}

const TaskNote: React.FC<TaskNoteProps> = ({
  values,
  setValues,
  triggerSave,
  setChanged,
  inModal
}) => {
  const dispatch = useAppDispatch()

  const {
    setAction,
    setReason,
    setSubtext,
    setTitle: setCloseTitle,
    setV2
  } = useAreYouSure()

  const [showingFiles, setShowingFiles] = React.useState(true)
  const [loading, setLoading] = React.useState(false)
  const [viewingID, setViewingID] = React.useState<string>(null)
  const [title, setTitle] = React.useState('')
  const [internalValue, setInternalValue] = React.useState(replaceWithBr(''))

  const media = React.useMemo(() => values.media || [], [values])

  const handleStartEdit = React.useCallback(
    async (id: string) => {
      setViewingID(id)
      setLoading(true)

      if (id === null) {
        setLoading(false)
        setShowingFiles(false)
        return
      }

      track('TASK_NOTE_VIEWED', { task_id: values.id, note_id: id })

      try {
        const something = await getMedia(id)

        const reader = new FileReader()
        // Load media and update internal value and title
        reader.onload = () => {
          setLoading(false)

          const existingMedia = media.find(m => m.id === id)
          setTitle(existingMedia.filename.replace(FILE_EXTENSION, ''))
          setInternalValue(reader.result as string)
          setShowingFiles(false)
        }
        reader.readAsText(something)
      } catch (e) {
        dispatch(AddErrorAlert({ e }))
      }
    },
    [media, dispatch, values]
  )

  const handleCreateClick = React.useCallback(() => {
    setChanged(true)
    handleStartEdit(null)
    track('TASK_NOTE_CREATE_NEW', { task_id: values.id })
  }, [setChanged, handleStartEdit, values])

  const handleCancelClick = React.useCallback(() => {
    if (!inModal) {
      setChanged(false)
    }

    setShowingFiles(true)
    setViewingID(null)
    setTitle('')
    setInternalValue(replaceWithBr(''))
  }, [inModal, setChanged])

  const handleSaveClick = React.useCallback(async () => {
    const newTitle = `${title.trim()}${FILE_EXTENSION}`

    // Create file
    const file = new File([internalValue], newTitle, {
      type: 'text/plain'
    })

    // Upload file
    const resp = await uploadFileTask(file)

    if (viewingID) {
      track('TASK_NOTE_EDITED', { task_id: values.id })

      // Remove id from media
      setValues(old => {
        // @ts-expect-error Property exists
        const modified = [...(old.file ?? [])].map(f =>
          f.id === viewingID ? resp : f
        )

        return {
          ...old,
          file: modified,
          media: modified
        }
      })
    } else {
      track('TASK_NOTE_CREATED', { task_id: values.id })

      // Add new media to task
      setValues(old => {
        // @ts-expect-error Property exists
        const modified = [...(old.file ?? []), resp]

        return {
          ...old,
          file: modified,
          media: modified
        }
      })
    }

    handleCancelClick()

    // Save task
    triggerSave()
  }, [
    values,
    handleCancelClick,
    internalValue,
    title,
    viewingID,
    setValues,
    triggerSave
  ])

  const handleDeleteClick = React.useCallback(
    (id: string) => {
      const action = () => {
        track('TASK_NOTE_DELETED', { task_id: values.id, note_id: id })

        setValues(old => {
          // @ts-expect-error Property exists
          const modified = [...(old.file ?? [])].filter(f => f.id !== id)

          return {
            ...old,
            file: modified,
            media: modified
          }
        })

        triggerSave()
      }

      setV2(true)
      setCloseTitle('DELETE NOTE?')
      setReason('Are you sure you want to delete this note?')
      setSubtext('This cannot be undone.')
      setAction(action)
    },
    [
      values,
      setValues,
      triggerSave,
      setV2,
      setCloseTitle,
      setReason,
      setSubtext,
      setAction
    ]
  )

  if (showingFiles || loading) {
    return (
      <Notes
        media={media}
        loading={loading}
        viewingID={viewingID}
        createNote={handleCreateClick}
        startEdit={handleStartEdit}
        deleteNote={handleDeleteClick}
      />
    )
  }

  return (
    <NoteEditor
      title={title}
      setTitle={setTitle}
      internalValue={internalValue}
      setInternalValue={setInternalValue}
      onCancel={handleCancelClick}
      onSave={handleSaveClick}
      setChanged={setChanged}
    />
  )
}

export default TaskNote
